import { booleanAttribute, Component, computed, effect, HostBinding, input, InputSignal, model, output, OutputEmitterRef, Renderer2, Signal, signal, viewChild, WritableSignal } from '@angular/core'
import { CdkVirtualScrollViewport, FixedSizeVirtualScrollStrategy, ScrollingModule } from '@angular/cdk/scrolling'

import { _SourceList_Base_ToolComponent } from './_source-list-base-tool.component'
import { ILevelGeneralListData } from '../../models/level-general-list-data'
import { ILevelGeneralListDto, levelGeneralListDto } from '../../models/level-general-list-dto'
import { IListButtonData, listButtonData } from 'src/app/features/shared/lists/models/_base-list-button'
import { ListButtonAdd } from 'src/app/features/shared/lists/models/list-button-add'
import { claimName } from 'src/assets/common/enums/claim-name.g'
import { ListButtonSave } from 'src/app/features/shared/lists/models/list-button-save'
import { ListButtonEdit } from 'src/app/features/shared/lists/models/list-button-edit'
import { MatSelectionListChange } from '@angular/material/list'
import { tabMode, TTabMode, TTabModeDeleted } from '../../models/tab-mode'
import { inactiveMode, TInactiveMode } from '../../models/inactive-mode'
import { listItem_isInactive } from '../../functions/list-item-is-inactive.function'
import { LogService } from '../../services/log.service'

export class CustomVirtualScrollStrategy extends FixedSizeVirtualScrollStrategy {
  constructor() {
    super(50, 500, 1000)
  }
}

@Component({
    selector: 'app-navigation-list-component',
    template: '',
    styles: [],
    imports: [
        ScrollingModule,
    ],
})
export abstract class _NavigationList_Base_ToolComponent
    extends _SourceList_Base_ToolComponent
{
    abstract btnAddData$$: WritableSignal<IListButtonData | null>
    abstract btnDeleteData$$: WritableSignal<ListButtonEdit | null>
    abstract btnDeleteDeepData$$: WritableSignal<ListButtonEdit | null>
    abstract btnRestoreListData$$: WritableSignal<ListButtonEdit | null>
    abstract buttonsHide$$: WritableSignal<boolean | null>
    abstract itemLines$$: WritableSignal<number | null>
    abstract itemSize$$: WritableSignal<number | null>
    abstract list$$: WritableSignal<ILevelGeneralListData[]>
    abstract listActive$$: Signal<ILevelGeneralListData[]>
    abstract listDeleted$$: Signal<ILevelGeneralListData[]>
    abstract noClick$$: WritableSignal<boolean | null>
    abstract tabMode$$: WritableSignal<TTabMode | null>

    abstract input_btnAddData$$: InputSignal<IListButtonData | null>
    abstract input_btnDeleteData$$: InputSignal<IListButtonData | null>
    abstract input_buttonsHide$$: InputSignal<boolean | null>
    abstract input_itemLines$$: InputSignal<number | null>
    abstract input_itemSize$$: InputSignal<number | null>
    abstract input_noClick$$: InputSignal<boolean | null>

    abstract out_onAdd: OutputEmitterRef<void>
    abstract out_onDeleteDeep: OutputEmitterRef<ILevelGeneralListData[]>
    abstract out_onRestore: OutputEmitterRef<ILevelGeneralListData[]>
    abstract out_onSave: OutputEmitterRef<void>

    abstract emit_btnAdd_click(): void;
    abstract emit_btnDeleteDeep_click(): void;
    abstract emit_btnRestore_click(): void;
    abstract emit_change(event: MatSelectionListChange): void;
    abstract emit_item_click(event: MouseEvent, item: ILevelGeneralListDto): void
    abstract emit_onScrollEnd(): void;
    abstract emit_toggleDeleted(mode?: TTabModeDeleted): void;
    abstract isActivated(item: ILevelGeneralListData): boolean;
    abstract toggleDeleted(mode?: TTabModeDeleted): void;
    
    protected _input_btnAddData$$ = input<IListButtonData | null>({}, {alias: 'btnAddData'})
    protected _input_btnDeleteData$$ = input<IListButtonData | null>({}, {alias: 'btnDeleteData'})
    // true: de deleted/inactive-lijstknop volgt alleen de data, niet de claim. Voor lijsten waarvan de
    // backend zelf al filtert wie de deleted/inactive-rijen krijgt (bv. FunctionMembers: claim
    // FunctionMembers/SuperiorData/Read + EntityOwner voor eigen functies): wat binnenkomt, mag je zien.
    protected _input_deletedListByData$$ = input(false, {transform: booleanAttribute, alias: 'deletedListByData'})
    // Hoe inactieve (niet-deleted) rijen getoond worden — zie inactive-mode.ts. 'list' volgt de verdeling
    // van de _ListService (ook eventuele overrides daar, bv. DomainUserInvitationListService); 'dimmed' en
    // 'hidden' verdelen zelf uit listAll op `deleted`, zodat de feature dat niet in een service hoeft te doen.
    protected _input_inactiveMode$$ = input<TInactiveMode>(inactiveMode.list, {alias: 'inactiveMode'})
    protected _input_buttonsHide$$ = input<boolean | null>(false, {alias: 'buttonsHide'})
    protected _input_itemLines$$ = input<number | null>(1, {alias: 'itemLines'})
    protected _input_itemSize$$ = input<number | null>(null, {alias: 'itemSize'})
    protected _input_noClick$$ = input<boolean | null>(false, {alias: 'noClick'})

    protected _out_onAdd = output({alias: 'onAdd'})
    protected _out_onDeleteDeep = output<ILevelGeneralListData[]>({alias: 'onDeleteDeep'})
    protected _out_onRestore = output<ILevelGeneralListData[]>({alias: 'onRestore'})
    protected _out_onSave = output({alias:'onSave'})

    protected _model_tabMode$$ = model<TTabMode>(tabMode.mainList, {alias: 'tabMode'})
    protected _btnAddData$$ = signal<ListButtonAdd | null>(null)
    protected _btnDeleteData$$ = signal<ListButtonEdit | null>(null)
    protected _btnDeleteDeepData$$ = signal<ListButtonSave | null>(null)
    protected _btnRestoreListData$$ = signal<ListButtonSave | null>(null)
    protected _buttonsHide$$ = signal<boolean | null>(null)
    protected _initial_tabMode$$ = signal<TTabMode | null>(null)
    protected _itemLines$$ = signal<number | null>(null)
    protected _itemSize$$ = signal<number | null>(null)
    protected override _list$$ = signal<ILevelGeneralListData[]>([])
    protected _noClick$$ = signal<boolean | null>(null)

    // Verdeling over hoofd-/deleted-/inactive-lijst volgens _input_inactiveMode$$ (tabel in inactive-mode.ts).
    private readonly __listListMode$$ = computed(() => this._input_inactiveMode$$() === inactiveMode.list)
    private readonly __listAllDeleted$$ = computed(() => this._listAll$$().filter(item => item[levelGeneralListDto.deleted]))
    protected override _listActive$$ = computed(() => {
        const __mode = this._input_inactiveMode$$()
        if (__mode === inactiveMode.list) return this._listService.listActive$$()

        const __notDeleted = this._listAll$$().filter(item => !item[levelGeneralListDto.deleted])
        return __mode === inactiveMode.hidden ? __notDeleted.filter(item => !listItem_isInactive(item)) : __notDeleted
    })
    protected override _listDeleted$$ = computed(() => this.__listListMode$$()
        ? this._listDeletedAll$$()?.filter(item => item[levelGeneralListDto.deleted] === true && item[levelGeneralListDto.inactive] === true)
        : this.__listAllDeleted$$())
    protected override _listInactive$$ = computed(() => this.__listListMode$$()
        ? this._listDeletedAll$$()?.filter(item => item[levelGeneralListDto.deleted] === false && item[levelGeneralListDto.inactive] === true)
        : [])

    private __deletedMode$$ = signal<TTabModeDeleted>(tabMode.deletedList)
    protected readonly _deletedMode$$ = this.__deletedMode$$.asReadonly()
    // Voor de "inactive"-knop in de lijst-templates: expliciet naar de inactieve lijst (de standaard-
    // modus van de wissel is de deleted-lijst).
    protected readonly _tabModeInactive: TTabModeDeleted = tabMode.inactiveList

    viewport$$ = viewChild(CdkVirtualScrollViewport)
    
    @HostBinding('class') class = 'tablist list-tool'
    
    constructor(
        protected _renderer?: Renderer2
    ) {
        super(_renderer)

        effect(() => {
            const __tabMode = this.tabMode$$()
            const __listActive = this._listActive$$()
            const __listDeleted = this._listDeleted$$()
            const __listInactive = this._listInactive$$()

            const __list = 
                __tabMode == tabMode.mainList ? __listActive 
                : __tabMode == tabMode.deletedList ? __listDeleted
                : __tabMode == tabMode.inactiveList ? __listInactive
                : []

            this._list$$.set(__list)
        })
        effect(() => {
            const _tabMode_model = this._model_tabMode$$()
            this._initial_tabMode$$.set(_tabMode_model)
        })
        effect(() => {
            const __tabMode = this._initial_tabMode$$()
            
            if (!!__tabMode && __tabMode !== tabMode.mainList) {this.__deletedMode$$.set(__tabMode)};
        })
        effect(() => {
            const __btnDeleteData = ListButtonAdd.create(this._input_btnDeleteData$$() ?? undefined)
            // 'list': de service beslist (die kan de knoppen bewust uitzetten); anders de eigen verdeling.
            const __listMode = this.__listListMode$$()
            const __hasDeletedList = __listMode ? this._listService.hasDeletedList$$() : this._listDeleted$$().length > 0
            const __hasInactiveList = __listMode ? this._listService.hasInactiveList$$() : false
            const __routeKeys = this._routeKeysClaim_get()
            const __claimPage = this._claimPageList$$()
            const __auth = this._input_deletedListByData$$()
                || this._applicationUserService.claim_check(__claimPage, __routeKeys, claimName.btnDeletedList_Show)

            // if (!__auth) LogService.envDev.logString("hiding button Delete", `no authorization: [claimPage.${__claimPage}], [claimName.${claimName.btnDeletedList_Show}]`)
            const __btnDeletedListHide = !(__auth && (__hasDeletedList || __hasInactiveList))
            
            this._btnDeleteData$$.set({...__btnDeleteData, hide: __btnDeletedListHide})
        })
        effect(() => {
            const __btnAddData =  ListButtonAdd.create(this._input_btnAddData$$() ?? undefined)
            const __routeKeys = this._routeKeysClaim_get()
            const __claimPage = this._claimPageList$$()
            const __auth = this._applicationUserService.claim_check(__claimPage, __routeKeys, claimName.btnListAdd_Show)
            // Expliciet [btnAddData]="{hide: true}" van de aanroeper wint van de claim (bv. FunctionMember-
            // lijsten: functies alleen via de checklists). ListButtonAdd.create zet hide standaard op true,
            // dus alleen de RUWE input telt hier — anders zou elke lijst zonder input verborgen blijven.
            const __hideRequested = this._input_btnAddData$$()?.[listButtonData.hide] === true
            // Idem disabled: recht aanwezig maar nu geblokkeerd (bv. een vergrendelde taak), met de reden als tooltip.
            const __disabledRequested = this._input_btnAddData$$()?.[listButtonData.disabled] === true

            // Recht aanwezig maar op deze omgeving vergrendeld (structuurpagina's buiten develop): disabled tonen.
            const __locked = !__auth && this._applicationUserService.claim_locked(__claimPage, claimName.btnListAdd_Show)

            // if (!__auth) LogService.envDev.logString("hiding button Add", `no authorization: [claimPage.${__claimPage}], [claimName.${claimName.btnListAdd_Show}]`)
            this._btnAddData$$.set({...__btnAddData, hide: (!__auth && !__locked) || __hideRequested, disabled: __locked || __disabledRequested})
        })
        effect(() => {
            const __btnRestoreData =  ListButtonSave.create({})
            const __routeKeys = this._routeKeysClaim_get()
            const __claimPage = this._claimPageList$$()
            const __auth = this._applicationUserService.claim_check(__claimPage, __routeKeys, claimName.btnListRestore_Show)

            // if (!__auth) LogService.envDev.logString("hiding button Restore", `no authorization: [claimPage.${__claimPage}], [claimName.${claimName.btnListRestore_Show}]`)
            this._btnRestoreListData$$.set({...__btnRestoreData, hide: !__auth})
        })
        effect(() => {
            const __btnDeleteDeepData = ListButtonSave.create({})
            const __routeKeys = this._routeKeysClaim_get()
            const __claimPage = this._claimPageList$$()
            const __auth = this._applicationUserService.claim_check(__claimPage, __routeKeys, claimName.btnListDeleteDeep_Show)

            // if (!__auth) LogService.envDev.logString("hiding button Delete deep", `no authorization: [claimPage.${__claimPage}], [claimName.${claimName.btnListDeleteDeep_Show}]`)
            this._btnDeleteDeepData$$.set({...__btnDeleteDeepData, hide: !__auth})
        })
        effect(() => {
            const __scrollElement = this._view_scrollElement$$()
            if (!!__scrollElement) {
                this._renderer?.listen(__scrollElement.nativeElement, 'scrollend', () => this.emit_onScrollEnd())
            }
        })
    }

    protected override _ngOnInit(): void {
        super._ngOnInit();

        this._buttonsHide$$.set(this._input_buttonsHide$$());
        this._itemLines$$.set(this._input_itemLines$$());
        this._itemSize$$.set(this._input_itemSize$$());
        this._noClick$$.set(this._input_noClick$$());
        this._initial_tabMode$$.set(this._model_tabMode$$());
    }

    protected override _emit_item_click(event: MouseEvent, item: ILevelGeneralListDto): void {
        super._emit_item_click(event, item);
    }

    protected _emit_toggleDeleted(mode?: TTabModeDeleted): void {
        this.toggleDeleted(mode);
    }

    protected _btnAdd_emit(): void {
        this._out_onAdd.emit();
    }

    protected _btnRestore_emit(): void {
        const __list = this.list$$().filter(item => !item[levelGeneralListDto.deleted]);

        // this.__restore$(__list).subscribe(_ => this.tabMode$$.set(tabMode.mainList));
        this._out_onRestore.emit(__list);
    }

    protected _btnDeleteDeep_emit(): void {
        const __list = this.list$$().filter(item => !item[levelGeneralListDto.deleted]);

        this._out_onDeleteDeep.emit(__list);
    }

    protected _btnRestore_setEnable(value: boolean): void {
        const __listData = this._btnRestoreListData$$() ?? ListButtonSave.create(null);

        this._btnRestoreListData$$.set({...__listData, disabled: !value});

        const __deleteDeepData = this._btnDeleteDeepData$$() ?? ListButtonSave.create(null);

        this._btnDeleteDeepData$$.set({...__deleteDeepData, disabled: !value});
    }

    // Rij gedimd tonen (class list-item--inactive, list.scss): alleen in de hoofdlijst — in de deleted-/
    // inactive-weergave is elke rij al niet-actief.
    protected _rowInactive(item: ILevelGeneralListData): boolean {
        return this._model_tabMode$$() === tabMode.mainList && listItem_isInactive(item)
    }

    protected _isActivated(item: ILevelGeneralListData): boolean {
        return item != null && this.activeGuid$$() == item.guid        
    }

    protected override _onScrollEnd(): void {
        // if (this._isRestoringScroll) return;

        let _position = this.viewport$$()?.measureScrollOffset('top') ?? 0;
        _position = Math.round(_position);

        this._tabDataService.ScrollPos = _position;
    }

    protected override _position_scrollTo(scrollPosition?: number | null): void {
        const _scrollPos = scrollPosition ?? this._tabDataService.ScrollPos ?? 0;
        setTimeout(() => {
            this.viewport$$()?.scrollTo({ top: _scrollPos })
        });
    }

    // mode: welke niet-actieve lijst de knop opent (deleted of inactive); zonder = de standaard-modus.
    // Terug naar de hoofdlijst gaat altijd, ongeacht mode.
    protected _toggleDeleted(mode?: TTabModeDeleted): void {
        const __currentMode = this._model_tabMode$$();
        const __deletedMode = mode ?? this._deletedMode$$();
        const __leaving = __currentMode !== tabMode.mainList;
        const __newMode = __leaving ? tabMode.mainList : __deletedMode;

        if (__leaving) { this.__selection_reset(__currentMode) }

        this._model_tabMode$$.set(__newMode);
    }

    private __selection_reset(mode: TTabMode): void {
        const __deleted = mode === tabMode.deletedList;

        this.list$$().forEach(item => item[levelGeneralListDto.deleted] = __deleted);
        this._listService.list_refresh$();
        this._btnRestore_setEnable(false);
    }
}
